import { useMemo, useEffect, useRef, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, TrendingUp, TrendingDown, Percent, Wallet, PieChart as PieChartIcon, Target, BarChart2, AlertCircle } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useI18n } from '@/i18n/use-i18n';
import {
  contributionsService,
  dashboardService,
  type DashboardData,
} from '@/services';
import { formatCurrency } from '@/utils/format-currency';
import './dashboard-studio.css';

type AllocationSlice = {
  category: string;
  percent: number;
  color: string;
  value: number;
};

type GoalComparison = {
  category: string;
  currentPercent: number;
  goalPercent: number;
  diff: number;
  color: string;
};

const resolveCssVar = (value: string): string => {
  const match = value.match(/^var\((--[^)]+)\)$/);
  if (!match) return value;
  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || value;
};

export function DashboardStudio() {
  const { strings } = useI18n();
  const queryClient = useQueryClient();
  const t = strings.dashboardStudio;
  const resolvedColorsRef = useRef<Record<string, string>>({});

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isFetching: isDashboardFetching,
    isError: isDashboardError,
    error: dashboardError,
  } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: dashboardService.getDashboard,
  });

  const consolidatePortfolioMutation = useMutation({
    mutationFn: () => contributionsService.consolidatePortfolio(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['target-portfolio'] });
    },
  });

  useEffect(() => {
    resolvedColorsRef.current = {};
  }, []);

  const isLoading = isDashboardLoading;
  const needsConsolidation = Boolean(dashboardData?.portfolio?.needsToConsolidate);
  const isConsolidating = consolidatePortfolioMutation.isPending;

  const errorMessage = isDashboardError
    ? dashboardError instanceof Error ? dashboardError.message : t.error
    : '';

  const summary = dashboardData?.summary ?? {
    totalAssets: 0,
    totalInvested: 0,
    profitLoss: 0,
    profitability: 0,
    estimatedTickers: [] as string[],
  };

  // Allocation slices for donut chart
  const allocationSlices = useMemo<AllocationSlice[]>(() => {
    const allocations = dashboardData?.portfolio?.allocations ?? [];
    const total = allocations.reduce((sum, a) => sum + a.current, 0);
    if (total <= 0) return [];

    return allocations
      .filter((a) => a.current > 0)
      .map((a) => {
        const raw = a.color;
        if (!resolvedColorsRef.current[raw]) {
          resolvedColorsRef.current[raw] = resolveCssVar(raw);
        }
        return {
          category: String(a.category),
          percent: (a.current / total) * 100,
          color: resolvedColorsRef.current[raw],
          value: a.current,
        };
      });
  }, [dashboardData]);

  const topAssets = dashboardData?.topAssets ?? [];

  // Goals comparison bars
  const goalsComparison = useMemo<GoalComparison[]>(() => {
    const allocations = dashboardData?.portfolio?.allocations ?? [];
    const totalCurrent = allocations.reduce((sum, a) => sum + a.current, 0);
    if (totalCurrent <= 0) return [];

    const totalGoal = allocations.reduce((sum, a) => sum + a.goal, 0);

    return allocations.map((a) => {
      const currentPercent = (a.current / totalCurrent) * 100;
      const goalPercent = totalGoal > 0 ? (a.goal / totalGoal) * 100 : 0;
      return {
        category: String(a.category),
        currentPercent,
        goalPercent,
        diff: currentPercent - goalPercent,
        color: a.color,
      };
    });
  }, [dashboardData]);

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  return (
    <Layout>
      <div className="dashboard-studio app-page">
        {needsConsolidation && !isLoading && (
          <div className="dashboard-studio__toast" role="status" aria-live="polite">
            <div className="dashboard-studio__toast-copy">
              <strong>{t.consolidation.title}</strong>
              <span>{t.consolidation.text}</span>
            </div>
            <button
              type="button"
              className="dashboard-studio__toast-button"
              onClick={() => consolidatePortfolioMutation.mutate()}
              disabled={isConsolidating}
            >
              {isConsolidating ? t.consolidation.loading : t.consolidation.action}
            </button>
          </div>
        )}

        {errorMessage && (
          <div className="dashboard-studio__feedback" role="alert">
            {errorMessage}
          </div>
        )}

        {/* Summary Cards */}
        <section className="ds-summary" style={{ '--delay': '0s' } as CSSProperties}>
          <div className="ds-summary__card">
            <div className="ds-summary__card-header">
              <span className="ds-summary__card-icon-wrap ds-summary__card-icon-wrap--brand">
                <Landmark size={16} />
              </span>
              <span className="ds-summary__card-label">{t.summaryCards.totalAssets}</span>
            </div>
            {isLoading ? (
              <div className="ds-skeleton ds-skeleton--lg" />
            ) : (
              <strong className="ds-summary__card-value">{formatCurrency(summary.totalAssets)}</strong>
            )}
          </div>

          <div className="ds-summary__card">
            <div className="ds-summary__card-header">
              <span className="ds-summary__card-icon-wrap ds-summary__card-icon-wrap--info">
                <Wallet size={16} />
              </span>
              <span className="ds-summary__card-label">{t.summaryCards.totalInvested}</span>
            </div>
            {isLoading ? (
              <div className="ds-skeleton ds-skeleton--lg" />
            ) : (
              <>
                <strong className="ds-summary__card-value">{formatCurrency(summary.totalInvested)}</strong>
                {summary.estimatedTickers.length > 0 && (
                  <span className="ds-summary__estimated-warning" title={t.summaryCards.estimatedWarning(summary.estimatedTickers.join(', '))}>
                    <AlertCircle size={12} />
                    {t.summaryCards.estimatedWarning(summary.estimatedTickers.join(', '))}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="ds-summary__card">
            <div className="ds-summary__card-header">
              <span className={`ds-summary__card-icon-wrap ${summary.profitLoss >= 0 ? 'ds-summary__card-icon-wrap--success' : 'ds-summary__card-icon-wrap--danger'}`}>
                {summary.profitLoss >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              </span>
              <span className="ds-summary__card-label">{t.summaryCards.profitLoss}</span>
            </div>
            {isLoading ? (
              <div className="ds-skeleton ds-skeleton--lg" />
            ) : (
              <strong className={`ds-summary__card-value ${summary.profitLoss >= 0 ? 'ds-value--positive' : 'ds-value--negative'}`}>
                {formatCurrency(summary.profitLoss)}
              </strong>
            )}
          </div>

          <div className="ds-summary__card">
            <div className="ds-summary__card-header">
              <span className={`ds-summary__card-icon-wrap ${summary.profitability >= 0 ? 'ds-summary__card-icon-wrap--warning' : 'ds-summary__card-icon-wrap--danger'}`}>
                <Percent size={16} />
              </span>
              <span className="ds-summary__card-label">{t.summaryCards.profitability}</span>
            </div>
            {isLoading ? (
              <div className="ds-skeleton ds-skeleton--lg" />
            ) : (
              <strong className={`ds-summary__card-value ${summary.profitability >= 0 ? 'ds-value--positive' : 'ds-value--negative'}`}>
                {formatPercent(summary.profitability)}
              </strong>
            )}
          </div>
        </section>

        {/* Middle Row: Allocation Chart + Top Assets */}
        <div className="ds-middle" style={{ '--delay': '0.05s' } as CSSProperties}>
          {/* Allocation Donut Chart */}
          <section className="ds-panel ds-panel--chart">
            <h2 className="ds-panel__title">{t.allocationChart.title}</h2>
            {isLoading ? (
              <div className="ds-chart-skeleton" />
            ) : allocationSlices.length > 0 ? (
              <div className="ds-chart">
                <div className="ds-chart__donut">
                  <PieChart width={220} height={220}>
                    <Pie
                      data={allocationSlices}
                      cx={110}
                      cy={110}
                      innerRadius={68}
                      outerRadius={95}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="percent"
                      nameKey="category"
                      stroke="none"
                    >
                      {allocationSlices.map((slice) => (
                        <Cell key={slice.category} fill={slice.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(_value: number | undefined, name: string | undefined, props) => [
                        formatCurrency((props.payload as AllocationSlice)?.value ?? 0),
                        name,
                      ]}
                      contentStyle={{
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  </PieChart>
                </div>
                <ul className="ds-chart__legend">
                  {allocationSlices.map((slice) => (
                    <li key={slice.category} className="ds-chart__legend-item">
                      <span className="ds-chart__legend-dot" style={{ background: slice.color }} />
                      <span className="ds-chart__legend-label">{slice.category}</span>
                      <strong className="ds-chart__legend-value">{slice.percent.toFixed(1)}%</strong>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="ds-empty-state">
                <span className="ds-empty-state__icon"><PieChartIcon size={28} /></span>
                <strong className="ds-empty-state__title">Sem alocação configurada</strong>
                <p className="ds-empty-state__text">Configure suas classes de ativos para ver a distribuição da sua carteira.</p>
                <Link to="/asset-classes" className="ds-empty-state__cta">Configurar carteira meta</Link>
              </div>
            )}
          </section>

          {/* Top Assets */}
          <section className="ds-panel ds-panel--assets">
            <h2 className="ds-panel__title">{t.topAssets.title}</h2>
            {isLoading ? (
              <div className="ds-assets-skeleton">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`skel-${i}`} className="ds-asset-skeleton-row">
                    <div className="ds-skeleton ds-skeleton--md" />
                    <div className="ds-skeleton ds-skeleton--sm" />
                  </div>
                ))}
              </div>
            ) : topAssets.length > 0 ? (
              <ul className="ds-assets__list">
                {topAssets.map((asset) => (
                  <li key={asset.ticker} className="ds-assets__item">
                    <div className="ds-assets__item-left">
                      <strong className="ds-assets__ticker">{asset.ticker}</strong>
                      <span className="ds-assets__name">{asset.shortName}</span>
                    </div>
                    <div className="ds-assets__item-right">
                      <strong className="ds-assets__value">{formatCurrency(asset.currentValue)}</strong>
                      <span className={`ds-assets__change ${asset.changePercent >= 0 ? 'ds-value--positive' : 'ds-value--negative'}`}>
                        {asset.changePercent >= 0 ? '\u2197' : '\u2198'} {formatPercent(asset.changePercent)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ds-empty-state">
                <span className="ds-empty-state__icon"><BarChart2 size={28} /></span>
                <strong className="ds-empty-state__title">Nenhum ativo na carteira</strong>
                <p className="ds-empty-state__text">Adicione ativos à sua carteira para acompanhar seu patrimônio.</p>
                <Link to="/target-portfolio" className="ds-empty-state__cta">Adicionar ativos</Link>
              </div>
            )}
          </section>
        </div>

        {/* Goals vs Current Allocation */}
        <section className="ds-panel ds-panel--goals" style={{ '--delay': '0.1s' } as CSSProperties}>
          <h2 className="ds-panel__title">{t.goalsComparison.title}</h2>
          {isLoading ? (
            <div className="ds-goals-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={`goal-skel-${i}`} className="ds-goal-skeleton-row">
                  <div className="ds-skeleton ds-skeleton--sm" />
                  <div className="ds-skeleton ds-skeleton--bar" />
                </div>
              ))}
            </div>
          ) : goalsComparison.length > 0 ? (
            <div className="ds-goals__list">
              {goalsComparison.map((item) => {
                const maxValue = Math.max(item.currentPercent, item.goalPercent, 0.1);
                const barWidth = (item.currentPercent / maxValue) * 100;
                return (
                  <div key={item.category} className="ds-goals__row">
                    <div className="ds-goals__row-header">
                      <span className="ds-goals__label">{item.category}</span>
                      <div className="ds-goals__values">
                        <span>{item.currentPercent.toFixed(1)}% / {item.goalPercent.toFixed(0)}%</span>
                        <span className={item.diff >= 0 ? 'ds-value--positive' : 'ds-value--negative'}>
                          ({item.diff >= 0 ? '+' : ''}{item.diff.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div className="ds-goals__bar-track">
                      <div
                        className="ds-goals__bar-fill"
                        style={{ width: `${Math.min(barWidth, 100)}%`, background: item.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="ds-empty-state">
              <span className="ds-empty-state__icon"><Target size={28} /></span>
              <strong className="ds-empty-state__title">Nenhuma meta configurada</strong>
              <p className="ds-empty-state__text">Defina os percentuais da sua carteira meta para ver a comparação com a alocação atual.</p>
              <Link to="/asset-classes" className="ds-empty-state__cta">Definir metas</Link>
            </div>
          )}
        </section>

        {isDashboardFetching && dashboardData && (
          <span className="ds-refreshing" aria-live="polite">{t.toolbar.refreshing}</span>
        )}
      </div>
    </Layout>
  );
}
